import React from 'react';
import { shortenEmail } from '../utils/emailParsing';

/**
 * AccountTabs — a single, horizontally-scrollable strip of mailbox/view tabs.
 *
 * Kept compact so it can live inside the top bar (saves a full row of vertical
 * space). System tabs first, then the warmup tab (only when relevant), then a
 * thin divider and the per-account tabs (only when more than one account).
 * Pure & presentational; selection state is owned by the orchestrator.
 */
const SYSTEM_TABS = [
  { id: 'all', label: 'All' },
  { id: 'leads', label: 'Leads' },
  { id: 'bounced', label: 'Bounced' },
  { id: 'sent', label: 'Sent' },
  { id: 'replied', label: 'Replied' },
  { id: 'forwarded', label: 'Forwarded' },
  { id: 'scheduled', label: 'Scheduled' },
  { id: 'safeguard', label: 'SafeGuard' },
  { id: 'deleted', label: 'Deleted' },
];

export default function AccountTabs({ selectedTab, onSelectTab, accounts = [], counts = {} }) {
  const countFor = (id) => {
    switch (id) {
      case 'all': return counts.total;
      case 'leads': return counts.leads;
      case 'bounced': return counts.bounced;
      case 'sent': return counts.sent;
      case 'replied': return counts.replied;
      case 'scheduled': return counts.scheduled;
      case 'safeguard': return counts.safeguard;
      case 'deleted': return counts.deleted;
      default: return null;
    }
  };

  // Number of new (unread) mails a tab should surface as a notification badge,
  // so it's obvious at a glance *where* new mail landed. Each figure excludes
  // exactly what its tab hides, so the badges never double-count each other:
  //   All  = inbox unread minus lead + bounce unread (those have own tabs)
  //   Leads / Bounced = their own unread only.
  const unreadFor = (id) => {
    switch (id) {
      case 'all':
        return Math.max(
          0,
          (counts.unread || 0) - (counts.leadsUnread || 0) - (counts.bounceUnread || 0)
        );
      case 'leads': return counts.leadsUnread || 0;
      case 'bounced': return counts.bounceUnread || 0;
      default: return 0;
    }
  };

  const renderTab = (id, label, opts = {}) => {
    const c = opts.count;
    const unread = opts.unread || 0;
    // Bounce notices are automated delivery reports, not mail to act on. Their
    // badge stays (so nothing disappears silently) but in a muted style, so it
    // never competes with real new mail for attention.
    const mutedUnread = id === 'bounced';
    const active = selectedTab === id;
    // `reserve` keeps a fixed-width count slot even before counts have loaded,
    // so the nav doesn't jump when the number pops in (used for "All").
    const showCount = opts.reserve || c > 0;
    const unreadTitle = mutedUnread
      ? `${unread} new delivery report${unread === 1 ? '' : 's'}`
      : `${unread} new`;
    return (
      <button
        key={id}
        type="button"
        className={`ivx-tab ${active ? 'active' : ''} ${opts.account ? 'ivx-tab-account' : ''} ${mutedUnread ? 'ivx-tab-quiet' : ''}`}
        onClick={() => onSelectTab(id)}
        title={unread > 0 ? unreadTitle : (opts.title || label)}
      >
        {label}
        {unread > 0 && (
          <span className={`ivx-tab-unread ${mutedUnread ? 'muted' : ''}`} aria-label={unreadTitle}>{unread}</span>
        )}
        {showCount && (
          <span className={`ivx-tab-count ${opts.reserve ? 'reserve' : ''}`}>{c > 0 ? c : ''}</span>
        )}
      </button>
    );
  };

  return (
    <div className="ivx-tabs">
      {renderTab('all', 'All', { count: countFor('all'), unread: unreadFor('all'), reserve: true })}
      {SYSTEM_TABS.filter((t) => t.id !== 'all').map((tab) => renderTab(tab.id, tab.label, { count: countFor(tab.id), unread: unreadFor(tab.id) }))}

      {counts.warmup > 0 && renderTab('warmup', 'Warmup', { count: counts.warmup, title: 'Background warmup mail' })}

      {accounts.length > 1 && (
        <>
          <span className="ivx-tabs-divider" aria-hidden="true" />
          {accounts.map((acc) =>
            renderTab(acc.id, shortenEmail(acc.email, 16), { account: true, title: acc.email })
          )}
        </>
      )}
    </div>
  );
}
